import React from 'react';
import { History } from 'lucide-react';
import { Player, Language } from '../../types';
import { formatCurrency } from '../../services/footballService';
import { getTeamName } from '../../services/teamService';
import { t } from '../../i18n';

interface TransferHistoryTimelineProps {
    history: Player['history'];
    lang: Language;
    title: string;
}

export const TransferHistoryTimeline: React.FC<TransferHistoryTimelineProps> = ({ history, lang, title }) => {
    // 시즌(연도)과 클럽이 동일하면 마지막 데이터만 남김
    const processedHistory = history
        .sort((a, b) => {
            const getYear = (s: string) => {
                const match = s.match(/\d{4}/);
                return match ? parseInt(match[0]) : 0;
            };
            return getYear(b.seasons) - getYear(a.seasons); 
        })
        .reduce((acc, current) => {
            const isDuplicate = acc.find(item => 
                item.club === current.club && 
                item.seasons === current.seasons
            );
            if (!isDuplicate) acc.push(current);
            return acc;
        }, [] as typeof history);

    return (
        <section className="w-full">
            <div className="flex items-center gap-3 mb-10">
                <div className="w-1 h-6 bg-slate-900 rounded-full"></div>
                <h2 className="text-lg font-black text-slate-900 tracking-tight uppercase leading-none">{t(lang, 'transferHistory')}</h2>
            </div>
            
            <div className="relative pl-8 border-l-2 border-slate-100 space-y-10 ml-2">
                {processedHistory.length > 0 ? (
                    processedHistory.map((item, i) => (
                        <div key={i} className="relative group">
                            {/* Sophisticated Node Design */}
                            <div className="absolute -left-[41px] top-1 flex items-center justify-center">
                                <div className="w-5 h-5 rounded-full bg-white border-2 border-slate-200 md:group-hover:border-rose-500 md:group-hover:scale-110 transition-all duration-300 shadow-sm flex items-center justify-center z-10">
                                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 md:group-hover:bg-rose-500 transition-colors"></div>
                                </div>
                                {/* Pulse effect for the latest/top item */}
                                {i === 0 && (
                                    <div className="absolute inset-0 w-5 h-5 rounded-full bg-rose-500/20 animate-ping pointer-events-none"></div>
                                )}
                            </div>
                            
                            <div className="flex items-center justify-between gap-6 p-4 -m-4 rounded-3xl md:hover:bg-slate-50/50 transition-all duration-500 md:group-hover:translate-x-1">
                                <div className="flex items-center gap-5 min-w-0">
                                    <div className="w-14 h-14 p-2 bg-white rounded-2xl flex items-center justify-center shrink-0 shadow-sm border border-slate-100 transition-transform md:group-hover:rotate-2">
                                        <img 
                                            src={item.clubLogo} 
                                            alt={item.club} 
                                            className="w-full h-full object-contain"
                                            onError={(e) => { e.currentTarget.style.opacity = '0.2'; }}
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest bg-rose-50 px-2 py-0.5 rounded-lg">
                                                {item.seasons}
                                            </span>
                                            <span className="text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">{item.type}</span>
                                        </div>
                                        <h3 className="font-black text-slate-900 text-sm md:text-base leading-tight truncate uppercase tracking-tight">
                                            {getTeamName(item.clubId, lang, item.club)}
                                        </h3>
                                    </div>
                                </div>
                                
                                <div className="text-right shrink-0">
                                    <span className="block text-xs font-black text-slate-900 tabular-nums">
                                        {item.fee === '-' || (item.fee?.toLowerCase() || '').includes('free') 
                                            ? <span className="text-slate-300 uppercase text-[9px] font-bold tracking-widest">{t(lang, 'freeAgent')}</span> 
                                            : formatCurrency(item.fee, lang)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                     <div className="text-center py-16 opacity-20 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-100">
                         <History size={40} className="mx-auto mb-3" />
                         <p className="text-[10px] font-black uppercase tracking-[0.2em]">{t(lang, 'careerIntelNotAvailable')}</p>
                     </div>
                )}
            </div>
        </section>
    );
};
